import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthFrame } from "@/pages/Login";
import { usePageMeta } from "@/lib/seo";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} required {...props} />
  </div>
);

export default function Signup() {
  usePageMeta("signup");
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ business_name: "", name: "", email: "", password: "", plan_id: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => {
      setPlans(data);
      const want = params.get("plan");
      const pick = data.find((p) => p.code === want) || data[0];
      if (pick) setForm((f) => ({ ...f, plan_id: pick.id }));
    });
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      const plan = plans.find((p) => p.id === form.plan_id);
      if (plan && plan.price > 0) {
        const { data: co } = await api.post("/payments/checkout", { plan_id: plan.id, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      }
      toast.success("Your business is ready");
      nav("/app");
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Start your rota" subtitle="Set up your business in under a minute.">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Field id="biz" label="Business name" value={form.business_name} onChange={set("business_name")} data-testid="signup-business-input" />
        <Field id="name" label="Your name" value={form.name} onChange={set("name")} data-testid="signup-name-input" />
        <Field id="email" label="Work email" type="email" value={form.email} onChange={set("email")} data-testid="signup-email-input" />
        <Field id="pw" label="Password" type="password" minLength={6} value={form.password} onChange={set("password")} data-testid="signup-password-input" />
        <div className="space-y-2">
          <Label>Plan</Label>
          <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
            <SelectTrigger data-testid="signup-plan-select"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
            <SelectContent>
              {plans.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`signup-plan-${p.code}`}>
                  {p.name} — {p.price > 0 ? `${money(p.price)}/mo` : "Free"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" className="h-11 w-full rounded-full" disabled={busy} data-testid="signup-submit-btn">
          {busy ? "Creating…" : "Create account"}
        </Button>
        <p className="text-center text-sm text-slate-500">
          Already have an account? <Link to="/login" className="font-semibold text-indigo-600" data-testid="goto-login-link">Sign in</Link>
        </p>
      </form>
    </AuthFrame>
  );
}
